import { ReactNode, useCallback, useState } from "react";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";

interface ActionMenuItem {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

interface ActionMenuProps {
  actions: ActionMenuItem[];
}

export default function ActionMenu({ actions }: ActionMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleItemClick = useCallback(
    (action: ActionMenuItem) => {
      handleClose();
      action.onClick();
    },
    [handleClose],
  );

  if (actions.length === 0) return null;

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label="More actions"
        aria-haspopup="true"
        size="small"
        sx={{ width: 32, height: 32, padding: "4px" }}
      >
        <MoreVertIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        slotProps={{ paper: { sx: { minWidth: 180 } } }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.label}
            onClick={() => handleItemClick(action)}
            disabled={action.disabled}
            sx={{
              minHeight: "44px",
              color: action.destructive ? "#DB382A" : undefined,
            }}
          >
            {action.icon && (
              <ListItemIcon
                sx={{ color: action.destructive ? "#DB382A" : undefined }}
              >
                {action.icon}
              </ListItemIcon>
            )}
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
